import React, { useEffect } from 'react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'hooks/useTranslations';
import { ILicenceAuthorityPrintoutSettingsFormValues } from 'models/licenceAuthority';
import { useAlert } from 'models/alertContext';
import useLicenceAuthorityPrintoutSettingsFormSchema, {
  MAX_HOST_FINGERPRINTS,
} from './schemaValidation';
import { Box, Grid, Typography } from '@mui/material';
import { Alert } from 'components/atoms/Alert';
import Button from 'components/atoms/Button';
import { EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { ActionTypes } from 'state/actions/alert';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { EIconTypes } from '../../../constants/Icons';
import { FormRadioGroup } from '../../../components/molecules/FormRadioGroup';
import { FormSelect } from 'components/molecules/FormSelect';

const SEND_HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  label: `${hour.toString().padStart(2, '0')}:00`,
  value: hour,
}));

enum ESendFrequency {
  onceADay = 'onceADay',
  twiceADay = 'twiceADay',
}

type TPrintoutSettingsFormValues = ILicenceAuthorityPrintoutSettingsFormValues & {
  sendFrequency: ESendFrequency;
};

const normalizeInitialFormValues = (
  values: ILicenceAuthorityPrintoutSettingsFormValues,
): TPrintoutSettingsFormValues => ({
  ...values,
  hostFingerprints: values.hostFingerprints?.length ? values.hostFingerprints : [''],
  sendFrequency:
    values.sendHours?.length > 1 ? ESendFrequency.twiceADay : ESendFrequency.onceADay,
});

interface ILicenceAuthoritiesPrintoutSettingsForm {
  initialFormValues: ILicenceAuthorityPrintoutSettingsFormValues;
  isSubmitLoading: boolean;
  onSubmit: (data: ILicenceAuthorityPrintoutSettingsFormValues) => void;
  onCancel: () => void;
}

const LicenceAuthoritiesPrintoutSettingsForm = ({
  initialFormValues,
  isSubmitLoading,
  onSubmit,
  onCancel,
}: ILicenceAuthoritiesPrintoutSettingsForm) => {
  const { t } = useTranslations();
  const { samePageAlert, clearAlert } = useAlert();

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { schema } = useLicenceAuthorityPrintoutSettingsFormSchema();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<TPrintoutSettingsFormValues>({
    defaultValues: normalizeInitialFormValues(initialFormValues),
    resolver: yupResolver(schema) as unknown as Resolver<TPrintoutSettingsFormValues>,
  });

  const hostFingerprints = useWatch({ control, name: 'hostFingerprints' }) ?? [''];
  const sendFrequency = useWatch({ control, name: 'sendFrequency' });
  const sendHours = useWatch({ control, name: 'sendHours' }) ?? [];

  const addHostFingerprint = () => {
    if (hostFingerprints.length >= MAX_HOST_FINGERPRINTS) return;

    setValue('hostFingerprints', [...hostFingerprints, ''], { shouldDirty: true });
  };

  const removeHostFingerprint = (index: number) => {
    if (hostFingerprints.length === 1) return;

    setValue(
      'hostFingerprints',
      hostFingerprints.filter((_, fingerprintIndex) => fingerprintIndex !== index),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const handleSendFrequencyChange = (target: EventTarget) => {
    const value = (target as HTMLInputElement).value;

    if (value === ESendFrequency.onceADay) {
      setValue('sendHours', sendHours.slice(0, 1), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const handleFormSubmit = (values: TPrintoutSettingsFormValues) => {
    const { sendFrequency, ...requestData } = values;

    const expectedHours = sendFrequency === ESendFrequency.twiceADay ? 2 : 1;

    const sendHours = requestData.sendHours
      .slice(0, expectedHours)
      .filter((hour): hour is number => typeof hour === 'number');

    onSubmit({
      ...requestData,
      sendHours,
    });
  };

  useEffect(() => {
    reset(normalizeInitialFormValues(initialFormValues));
  }, [initialFormValues, reset]);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      {samePageAlert && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}
      <Grid container size={{ xs: 12 }} sx={{ rowGap: 3 }} columnSpacing="20px">
        <Grid size={{ xs: 12, md: 4 }}>
          <FormInput
            name="username"
            control={control}
            errors={errors}
            label={t('licenceAuthoritiesPrintoutSettings.username.label')}
            setValue={setValue}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormInput
            name="host"
            control={control}
            errors={errors}
            label={t('licenceAuthoritiesPrintoutSettings.host.label')}
            setValue={setValue}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormInput
            name="port"
            control={control}
            errors={errors}
            label={t('licenceAuthoritiesPrintoutSettings.port.label')}
            setValue={setValue}
            required
          />
        </Grid>
        <Grid container size={{ xs: 12 }} sx={{ rowGap: 0.5 }}>
          {hostFingerprints.map((_, index) => (
            <Grid
              key={index}
              size={{ xs: 12, md: 8 }}
              sx={{ display: 'flex', alignItems: 'flex-start' }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <FormInput
                  name={`hostFingerprints.${index}`}
                  control={control}
                  errors={errors}
                  label={t('licenceAuthoritiesPrintoutSettings.fingerprints.label')}
                  setValue={setValue}
                  charsLimit={255}
                  maxLength
                  required
                  withLabel={index === 0}
                />
              </Box>

              {hostFingerprints.length > 1 && (
                <Button
                  variant={EButtonVariants.outlined}
                  tableButton
                  icon={EIconTypes.delete}
                  sizePX={EButtonSizesPX.px32}
                  onClick={() => removeHostFingerprint(index)}
                  sx={{
                    ml: 0.5,
                    mt: index === 0 ? '28px' : 0,
                    flexShrink: 0,
                  }}
                />
              )}
            </Grid>
          ))}

          {hostFingerprints.length < MAX_HOST_FINGERPRINTS && (
            <Grid size={{ xs: 12, md: 8 }}>
              <Button
                type="button"
                variant={EButtonVariants.contained}
                label={t('licenceAuthoritiesPrintoutSettings.add.another.label')}
                onClick={addHostFingerprint}
              />
            </Grid>
          )}
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormInput
            name="outboundPath"
            control={control}
            errors={errors}
            label={t('licenceAuthoritiesPrintoutSettings.outboundPath.label')}
            setValue={setValue}
            required
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormInput
            name="inboundPath"
            control={control}
            errors={errors}
            label={t('licenceAuthoritiesPrintoutSettings.inboudPath.label')}
            setValue={setValue}
            required
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormInput
            name="pgpPublicKey"
            type={EFormInputTypes.textarea}
            multiline
            control={control}
            errors={errors}
            label={t('licenceAuthoritiesPrintoutSettings.keyPrintoutSystem.label')}
            setValue={setValue}
            rows={6}
            charsLimit={100000}
            required
          />
        </Grid>
      </Grid>
      <Grid container size={{ xs: 12 }} columnSpacing="20px">
        <Grid size={{ xs: 12, md: 4 }}>
          <FormRadioGroup
            name="sendFrequency"
            label={t('licenceAuthoritiesPrintoutSettings.sendFrequency.label')}
            options={[
              {
                label: t(
                  'licenceAuthoritiesPrintoutSettings.firstPrintingHours.onceADay.label',
                ),
                value: ESendFrequency.onceADay,
              },
              {
                label: t(
                  'licenceAuthoritiesPrintoutSettings.firstPrintingHours.twiceADay.label',
                ),
                value: ESendFrequency.twiceADay,
              },
            ]}
            control={control}
            errors={errors}
            onChangeOption={handleSendFrequencyChange}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormSelect
            name="sendHours.0"
            control={control}
            errors={errors}
            label={t('licenceAuthoritiesPrintoutSettings.firstPrintingHours.label')}
            options={SEND_HOUR_OPTIONS}
            setValue={setValue}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormSelect
            name="sendHours.1"
            control={control}
            errors={errors}
            disabled={sendFrequency !== ESendFrequency.twiceADay}
            label={t('licenceAuthoritiesPrintoutSettings.secondPrintingHours.label')}
            options={SEND_HOUR_OPTIONS}
            setValue={setValue}
            required={sendFrequency !== ESendFrequency.twiceADay}
          />
        </Grid>
        {errors?.sendHours?.message && (
          <Grid size={{ xs: 12 }}>
            <FormErrorLabel style={{ mt: '-16px' }} label={errors?.sendHours?.message} />
          </Grid>
        )}
      </Grid>
      <Grid container columnSpacing="20px" sx={{ rowGap: 1, mt: '40px' }}>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Button
            type="submit"
            fullWidth
            label={t('general.save.button')}
            id="save"
            variant={EButtonVariants.contained}
            isLoading={isSubmitLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Button
            fullWidth
            label={t('general.cancel.button')}
            id="cancel"
            variant={EButtonVariants.outlined}
            onClick={onCancel}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
        </Grid>
      </Grid>
    </form>
  );
};

export default LicenceAuthoritiesPrintoutSettingsForm;
